import { useState, useEffect, ReactNode, forwardRef } from "react";
import classNames from "classnames";
import { useVariant } from "@~/contexts/VariantContext/VariantContext";
import { isM2MFabricProduct } from "./components/M2MProductSelector/utils";
import { M2MProductSelector } from "./components/M2MProductSelector/M2MProductSelector";
import { CardComponentProps } from "../../../types/types";
import styles from "./PDPLink.module.scss";
import { getProductUrl } from "@~/utils/product";

type PDPLinkProps = {
  product: CardComponentProps["product"];
  children: ReactNode;
  className?: string;
  componentName?: string;
  tabIndex?: number;
  testId?: string;
  ariaHidden?: boolean;
  ariaLabel?: string;
  noFollow?: boolean;
};

export const PDPLink = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  PDPLinkProps
>(
  (
    {
      product,
      children,
      className,
      componentName,
      tabIndex,
      testId,
      ariaHidden,
      ariaLabel,
      noFollow = false,
    },
    ref,
  ) => {
    const [showM2MModal, setShowM2MModal] = useState(false);
    const { variant } = useVariant();
    const isM2MProduct = isM2MFabricProduct(product);

    const handleM2MClick = () => {
      window.dispatchEvent(new CustomEvent("closeAllM2MModals"));
      setShowM2MModal(true);
    };

    const handleCloseModal = () => {
      setShowM2MModal(false);
    };

    useEffect(() => {
      const handleM2MModalClose = () => setShowM2MModal(false);
      window.addEventListener("closeAllM2MModals", handleM2MModalClose);
      return () =>
        window.removeEventListener("closeAllM2MModals", handleM2MModalClose);
    }, []);

    if (isM2MProduct) {
      return (
        <>
          <button
            ref={ref as React.Ref<HTMLButtonElement>}
            className={classNames(styles.container, className)}
            onClick={handleM2MClick}
            data-component={componentName}
            type="button"
            tabIndex={tabIndex}
            aria-label="Open product configuration modal"
          >
            {children}
          </button>
          {showM2MModal && (
            <M2MProductSelector
              isOpen={showM2MModal}
              onClose={handleCloseModal}
              pdpUrl={variant?.pdpURL.url || ""}
            />
          )}
        </>
      );
    }

    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={getProductUrl(product, variant)}
        className={classNames(styles.container, className)}
        data-component={componentName}
        data-testid={testId ?? undefined}
        tabIndex={tabIndex}
        aria-hidden={ariaHidden}
        aria-label={ariaLabel}
        {...(noFollow && { rel: "nofollow" })}
      >
        {children}
      </a>
    );
  },
);

PDPLink.displayName = "PDPLink";
